import { ObjectID } from "bson";
import create, { GetState, SetState } from "zustand";

import { SurveyQuestionJson } from "@performyard/service-contracts";

import { SurveyBuilderStore } from "./model";

const surveyBuilderStore = (set: SetState<SurveyBuilderStore>, get: GetState<SurveyBuilderStore>) => ({
	surveyName: "",
	invalidNameMessage: "",
	surveyQuestionsDraft: {},
	orderedQuestions: [],
	editingQuestion: null,
	invalidQuestionMessages: {},
	hasChanges: false,
	builderBanner: null,
	actions: {
		_validateName: (surveyName) => {
			return surveyName.length > 0;
		},
		sortQuestionsByOrder: () => {
			const orderedQuestions = Object.values(get().surveyQuestionsDraft)
				.filter((qq) => !qq.deleted)
				.sort((a, b) => a.order - b.order);
			const newQuestionsDraft = {};
			for (let i = 0; i < orderedQuestions.length; i++) {
				newQuestionsDraft[orderedQuestions[i]._id] = { ...orderedQuestions[i], order: i + 1 };
			}
			set({ orderedQuestions, surveyQuestionsDraft: newQuestionsDraft });
		},
		addSurveyQuestion: () => {
			const { actions, surveyQuestionsDraft, editingQuestion } = get();
			const existingQuestions = surveyQuestionsDraft;
			const newQuestion: SurveyQuestionJson = {
				_id: new ObjectID().toString(),
				type: "text",
				text: "",
				order: Object.keys(existingQuestions).length + 1,
			};
			set({
				surveyQuestionsDraft: { ...existingQuestions, [newQuestion._id]: newQuestion },
				editingQuestion: editingQuestion
					? editingQuestion
					: { _id: new ObjectID(newQuestion._id), isNew: true },
			});
			actions.sortQuestionsByOrder();
		},
		validateSurveyUpdate: (onValid?: (name: string) => void) => {
			const { surveyName, actions } = get();
			if (!actions._validateName(surveyName)) {
				set({ invalidNameMessage: "Survey name is required" });
				return false;
			}
			onValid && onValid(surveyName);
			return true;
		},
		validatePublishSurvey: (
			onValid: (questions: SurveyQuestionJson[], name: string) => void,
			onInvalid: () => void
		) => {
			const { actions, surveyName } = get();
			// validate at least one non-instructions question
			return actions.validateSurveyUpdate(() => {
				const { orderedQuestions } = get();
				const nonInstructionsQuestions = orderedQuestions.filter(
					(question) => question.type !== "instructions"
				);
				if (nonInstructionsQuestions.length === 0) {
					onInvalid();
					return false;
				}
				onValid(orderedQuestions, surveyName);
				return true;
			});
		},
		resetStore: () => {
			set({
				surveyName: "",
				invalidNameMessage: "",
				surveyQuestionsDraft: {},
				orderedQuestions: [],
				editingQuestion: null,
				invalidQuestionMessages: {},
				builderBanner: null,
				hasChanges: false,
			});
		},
	},
	setters: {
		setHasChanges: (hasChanges: boolean) => {
			set({ hasChanges });
		},
		setSurveyName: (surveyName) => {
			if (surveyName !== get().surveyName) {
				set({ surveyName, invalidNameMessage: "" });
			}
		},
		setEditingQuestion: (question) => {
			set({ editingQuestion: question });
		},
		setSurveyQuestionsDraft: (savedSurveyQuestions) => {
			const draft = savedSurveyQuestions.reduce((acc, question) => {
				acc[question._id] = question;
				return acc;
			}, {});
			set({ surveyQuestionsDraft: draft });
		},
		setBuilderBanner: (builderBanner) => {
			set({ builderBanner });
		},
		setInvalidNameMessage: (invalidNameMessage) => {
			set({ invalidNameMessage });
		},
		setInvalidQuestionMessages: (invalidQuestionMessages) => {
			set({ invalidQuestionMessages });
		},
		setOrderedQuestions: (questions) => {
			const questionsWithOrders = questions.map((question, index) => {
				const newQuestion = { ...question, order: index };
				if (question.options) {
					newQuestion.options = question.options.map((option, index) => ({ ...option, order: index }));
				}
				return newQuestion;
			});
			set({ orderedQuestions: questionsWithOrders });
			return questionsWithOrders;
		},
	},
});

export const useSurveyBuilderStore = create<SurveyBuilderStore>(surveyBuilderStore);

export const useOrderedQuestions = () => useSurveyBuilderStore((state) => state.orderedQuestions);
export const useSurveyQuestionsDraft = () => useSurveyBuilderStore((state) => state.surveyQuestionsDraft);
export const useSurveyName = () => useSurveyBuilderStore((state) => state.surveyName);
export const useEditingQuestion = () => useSurveyBuilderStore((state) => state.editingQuestion);
export const useInvalidNameMessage = () => useSurveyBuilderStore((state) => state.invalidNameMessage);
export const useInvalidQuestionMessages = () => useSurveyBuilderStore((state) => state.invalidQuestionMessages);
export const useHasChanges = () => useSurveyBuilderStore((state) => state.hasChanges);
export const useBuilderBanner = () => useSurveyBuilderStore((state) => state.builderBanner);
export const useActions = () => useSurveyBuilderStore((state) => state.actions);
export const useSetters = () => useSurveyBuilderStore((state) => state.setters);
